import * as Dfns from 'date-fns/fp';
import { useEffect, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Bar,
  ComposedChart,
  Line,
  ReferenceArea,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { TICK_STYLE, X_AXIS_PADDING } from '@Components/Charts/ChartConstants';
import { EmptyState } from '@Components/EmptyState';
import { Placement } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { PacingQuarterColors } from '@src/utils/colors';
import { ClearanceTooltip } from './components/ClearanceTooltip';
import { CsvDownloadButton } from './components/CsvDownloadButton';
import { CompanyLabel } from './components/PacingLabels';
import { LoadFailedState, RowsSkeleton } from './components/PacingSkeletons';
import { CLEARANCE_CHART_COLORS, CLEARANCE_CHART_MARGIN } from './linear-pacing-constants';
import { ClearanceRow, createSeriesData } from './linear-pacing-utils';
import { buildWeekRange, useClearance, useClearanceForecast } from './use-linear-clearance';

// Matches .forecast-row's min-height in LinearPacing.scss.
const FORECAST_ROW_HEIGHT = 184;

interface ForecastPoint {
  Forecast: number | undefined;
  Actual: number | null | undefined;
  week: string;
  company: ClearanceRow['company'];
  quarter: number;
  dark: boolean;
}

interface QuarterSpan {
  start: string;
  end?: string;
  quarter: number;
}

const formatPercent = (val: unknown) => `${val}%`;

// Broadcast quarters: for a given week (that starts on Monday), find the month of the Sunday of
// that week, then divide by 3.
const getQuarterInfo = (dateStr: string) => {
  const dateParts = dateStr.match(/(\d\d\d\d-\d\d-\d\d)$/);
  if (!dateParts) {
    throw new Error("Date wasn't parseable");
  }
  const weekStart = Dfns.parseISO(dateParts[1]);
  const weekEnd = Dfns.subDays(1, Dfns.addWeeks(1, weekStart));
  return Math.floor(Dfns.getMonth(weekEnd) / 3);
};

const finiteValues = (values: (number | null | undefined)[]) =>
  values.filter((val): val is number => Number.isFinite(val));

// Matches Ramda's R.min(a, b) (`b < a ? b : a`), including how it treats a missing value.
const ramdaMin = (a: number | undefined, b: number | undefined) =>
  a !== undefined && b !== undefined && b < a ? b : a;

const average = (values: number[]) =>
  Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);

const getMinY = (series: ForecastPoint[]) => {
  const lowestForecast = finiteValues(series.map(point => point.Forecast)).sort((a, b) => a - b)[0];
  const lowestActual = finiteValues(series.map(point => point.Actual)).sort((a, b) => a - b)[0];
  let minY = ramdaMin(lowestForecast, lowestActual) ?? NaN;
  if (minY % 10 === 0) {
    minY -= 10;
  } else {
    minY -= minY % 10;
  }
  return Math.max(0, minY);
};

const getQuarterSpans = (series: ForecastPoint[]) => {
  const quarters: QuarterSpan[] = [];
  series.forEach(({ week, quarter }) => {
    const last = quarters[quarters.length - 1];
    if (!last) {
      quarters.push({ start: week, quarter });
    } else if (last.quarter !== quarter) {
      last.end = week;
      quarters.push({ start: week, quarter });
    }
  });
  return quarters;
};

const mergeSeries = (
  forecastRows: ClearanceRow[],
  historicalRows: ClearanceRow[]
): ForecastPoint[] =>
  forecastRows.map((point, idx) => {
    const historicalPoint: Partial<ClearanceRow> = historicalRows[idx] ?? {};
    return {
      Forecast: point.rate,
      Actual: historicalPoint.clearance_rate,
      week: point.week,
      company: point.company,
      quarter: getQuarterInfo(point.week),
      dark: (historicalPoint.spend_expected ?? 0) === 0
    };
  });

const RectangleBar = ({
  fill,
  x,
  y,
  width,
  height,
  payload
}: {
  fill?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ForecastPoint;
}) => {
  if (!payload?.Actual) {
    return null;
  }
  return (
    <path d={`M${x} ${y} h ${width} v ${height} h ${-(width ?? 0)} Z`} stroke='none' fill={fill} />
  );
};

const ForecastDot = ({
  cx = 0,
  cy = 0,
  payload
}: {
  cx?: number;
  cy?: number;
  payload?: ForecastPoint;
}) => {
  if (!payload?.Forecast) {
    return null;
  }
  if (payload.dark) {
    return (
      <svg
        x={cx - 5}
        y={cy - 5}
        width={10}
        height={10}
        fill={CLEARANCE_CHART_COLORS.forecastDark}
        viewBox='0 0 1024 1024'
      >
        <line
          x1='0'
          y1='0'
          x2='1024'
          y2='1024'
          stroke={CLEARANCE_CHART_COLORS.forecastDark}
          strokeWidth='256'
        />
        <line
          x1='1024'
          y1='0'
          x2='0'
          y2='1024'
          stroke={CLEARANCE_CHART_COLORS.forecastDark}
          strokeWidth='256'
        />
      </svg>
    );
  }
  return (
    <svg
      x={cx - 5}
      y={cy - 5}
      width={10}
      height={10}
      fill={CLEARANCE_CHART_COLORS.clearance}
      viewBox='0 0 1024 1024'
    >
      <path d='M 512, 512 m -512, 0 a 512,512 0 1,0 1024,0 a 512,512 0 1,0 -1024,0' />
    </svg>
  );
};

const CompanyRow = ({
  company,
  series,
  range
}: {
  company: string;
  series: ForecastPoint[];
  range: string[];
}) => {
  const minY = getMinY(series);
  const quarters = getQuarterSpans(series);
  const avgForecast = average(finiteValues(series.map(point => point.Forecast)));
  const avgClearance = average(finiteValues(series.map(point => point.Actual)));

  return (
    <div className='forecast-row'>
      <CompanyLabel company={company} />
      <div className='clearance-chart forecast-chart'>
        <AutoSizer
          renderProp={({ width = 0, height = 0 }) => (
            <ComposedChart
              width={width}
              height={height}
              data={series}
              margin={CLEARANCE_CHART_MARGIN}
            >
              <XAxis
                dataKey='week'
                ticks={range}
                tickCount={range.length}
                axisLine={false}
                tickLine={false}
                padding={X_AXIS_PADDING}
                tick={TICK_STYLE}
                domain={[range[0], range[range.length - 1]]}
              />
              <YAxis
                tick={TICK_STYLE}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatPercent}
                domain={[minY, 100]}
              />
              <Bar
                dataKey='Actual'
                barSize={20}
                isAnimationActive={false}
                fill={CLEARANCE_CHART_COLORS.actual}
                shape={<RectangleBar />}
              />
              <Line
                type='monotone'
                dataKey='Forecast'
                isAnimationActive={false}
                stroke={CLEARANCE_CHART_COLORS.clearance}
                dot={<ForecastDot />}
              />
              <RechartsTooltip isAnimationActive={false} content={<ClearanceTooltip />} />
              {quarters.map(quarter => (
                <ReferenceArea
                  key={quarter.start}
                  x1={quarter.start}
                  x2={quarter.end}
                  y1={minY}
                  y2={100}
                  fill={PacingQuarterColors[quarter.quarter]}
                  opacity={0.25}
                />
              ))}
            </ComposedChart>
          )}
        />
      </div>
      <OverlayTrigger
        placement={Placement.TOP}
        overlay={<Tooltip id={`summary_${company}`}>Clearance % / Forecast %</Tooltip>}
      >
        <div className='avail-title'>
          <span className='actual-value'>{avgClearance}%</span> /{' '}
          <span className='forecast-value'>{avgForecast}%</span>
        </div>
      </OverlayTrigger>
    </div>
  );
};

export const LinearPacingForecast = ({
  startDate,
  endDate
}: {
  startDate: string;
  endDate: string;
}) => {
  const setError = useSetError();
  const range = useMemo(() => buildWeekRange(startDate, endDate), [startDate, endDate]);

  const forecastQuery = useClearanceForecast(startDate, endDate);
  const historicalQuery = useClearance({ group: 'company', start: startDate, end: endDate });

  const error = forecastQuery.error || historicalQuery.error;
  useEffect(() => {
    if (error) {
      setError({ message: 'Could not fetch data for forecast page', reportError: error });
    }
  }, [error, setError]);

  // One merged series per company that has both forecast and historical data, sorted by name.
  const mergedSeries = useMemo(() => {
    if (!forecastQuery.data || !historicalQuery.data) {
      return undefined;
    }
    const forecastByCompany = createSeriesData('company', forecastQuery.data, range).groupedByKey;
    const historicalByCompany = createSeriesData(
      'company',
      historicalQuery.data,
      range
    ).groupedByKey;
    return Object.keys(historicalByCompany)
      .filter(company => Object.hasOwn(forecastByCompany, company))
      .sort()
      .map(company => ({
        company,
        series: mergeSeries(forecastByCompany[company], historicalByCompany[company])
      }));
  }, [forecastQuery.data, historicalQuery.data, range]);

  return (
    <div className='pacing-dashboard'>
      <div className='pacing-panes'>
        <div className='pacing-left-pane'>
          <div className='pacing-pane'>
            {mergedSeries && mergedSeries.length > 0 && (
              <div className='pacing-pane-controls align-end'>
                <CsvDownloadButton
                  filename={`Linear clearance vs forecast ${startDate} to ${endDate}`}
                  rows={() => [
                    ['Company', 'Week', 'Quarter', 'Clearance', 'Forecast'],
                    ...mergedSeries.flatMap(({ company, series }) =>
                      series.map(point => [
                        company,
                        point.week,
                        `Q${point.quarter + 1}`,
                        Number.isFinite(point.Actual) ? `${point.Actual}%` : '',
                        Number.isFinite(point.Forecast) ? `${point.Forecast}%` : ''
                      ])
                    )
                  ]}
                />
              </div>
            )}
            <div className='pacing-pane-lines'>
              {!mergedSeries &&
                (error ? <LoadFailedState /> : <RowsSkeleton rowHeight={FORECAST_ROW_HEIGHT} />)}
              {mergedSeries?.length === 0 && (
                <EmptyState title='No forecast data for this date range' />
              )}
              {mergedSeries?.map(({ company, series }) => (
                <CompanyRow key={company} company={company} series={series} range={range} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
